import React, { useState, useEffect, useCallback } from 'react';
import { httpSymfony } from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import {
    Box,
    Paper,
    Typography,
    FormHelperText,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    IconButton,
    Tooltip,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { toBase64 } from 'Utils/utils';
import { process } from 'Utils/errorHandler';
import UploadLoanReworkedWithTabs from 'Components/loan-boarding/UploadLoanReworkedWithTabs';

const styles = {
    title: {
        textAlign: 'center',
        marginBottom: '25px',
        fontSize: '3rem',
        fontWeight: 500,
    },
    sectionTitle: {
        color: '#3B465C',
        marginBottom: '20px'
    },
    formContainer: {
        padding: '20px'
    },
    dropzoneContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '50px'
    },
    button: {
        padding: '18px 26px',
        fontSize: '16px',
        borderRadius: '4px',
    },
    jsonPreview: {
        overflow: 'auto',
        maxHeight: '300px'
    }
};

const getColor = (props) => {
    if (props.isDragAccept) {
        return '#00e676';
    }
    if (props.isDragReject) {
        return '#ff1744';
    }
    if (props.isFocused) {
        return '#2196f3';
    }
    return '#eeeeee';
};

const Container = styled('div')({
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: '2px',
    borderRadius: '2px',
    borderColor: `${(props) => getColor(props)}`,
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border 0.24s ease-in-out',
    cursor: 'pointer',
});
// TODO use shared component
const Dropzone = ({ multiple, onChange, ...rest }) => {
    const { getRootProps, getInputProps, acceptedFiles, isFocused, isDragAccept, isDragReject } = useDropzone({
        multiple,
        ...rest,
        accept: { 'text/xml': ['.xml'] },
        maxSize: '10485760',
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0 && onChange) {
                const file = acceptedFiles[0];
                onChange({ target: { files: [file] } });
            }
        },
    });

    const files = acceptedFiles.map((file) => {
        return <li key={file.path}>{file.name}</li>;
    });

    return (
        <div className='container'>
            <Container {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
                Upload file to be processed
                <input {...getInputProps({ onChange })} />
            </Container>
            <Box sx={{ position: 'absolute', paddingTop: '5px' }}>
                <ul>{files}</ul>
            </Box>
        </div>
    );
};

function BeautifyJson({ jsonStr }) {
    const beautifiedJson = JSON.stringify(JSON.parse(jsonStr), null, 4);

    return <pre style={{ overflow: 'auto', maxHeight: '300px' }}>{beautifiedJson}</pre>;
}

const MemoizedDropzone = React.memo(Dropzone);
const MemoizedBeautifyJson = React.memo(BeautifyJson);

const LoanBoardingPage = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [flowsList, setFlowsList] = useState([]);
    const [responseLoan, setResponseLoan] = useState(null);
    const [responseJson, setResponseJson] = useState(null);
    const [tabs, setTabs] = useState(null);
    const [fieldsConfig, setFieldsConfig] = useState(null);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({ mode: 'onChange' });

    const handleApiError = useCallback((error) => {
        process(error, enqueueSnackbar);
    }, [enqueueSnackbar]);

    const onSubmit = async (data) => {
        dispatch(setLoading(true));
        setResponseLoan(null);
        setResponseJson(null);
        setTabs(null);
        setFieldsConfig(null);

        try {
            const requestData = {
                flowId: data.flow_id,
                fileContent: await toBase64(data.uploaded_file),
            };

            const res = await httpSymfony.post(`/call/flow/${data.flow_id}`, requestData);

            enqueueSnackbar('File imported successfully', {
                action: CustomSnackbar,
                variant: 'success',
            });


            if (typeof res.data[0]?.data === 'string') {
                setResponseLoan(res.data[0]);
            } else {
                setResponseLoan(res.data[0].data);
                setTabs(res.data[0]['data (1)']);
                setFieldsConfig(res.data[0]['data (2)']);
            }
        } catch (err) {
            handleApiError(err);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const getListsFlows = useCallback(async () => {
        try {
            const res = await cachedGet('/user/flow/lbh/list');
            setFlowsList(res.data.flows);
        } catch (err) {
            handleApiError(err);
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch, handleApiError]);

    useEffect(() => {
        getListsFlows();
    }, [getListsFlows]);

    return (
        <>
            <Typography variant='h1' sx={styles.title}>
                Loan Boarding Hub
            </Typography>

            <Box component={Paper} sx={styles.formContainer}>
                <Box sx={{ marginBottom: '20px' }}>
                    <Typography variant='h6' sx={styles.sectionTitle}>
                        1. Import XML file
                    </Typography>

                    <Box component='form' onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                        }
                    }}>
                        <Box sx={styles.dropzoneContainer}>
                            <Box>
                                <FormControl
                                    sx={{ minWidth: '200px', marginRight: '20px' }}
                                    error={!!errors.uploaded_file}
                                >
                                    <Controller
                                        name="uploaded_file"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { onChange } }) => (
                                            <MemoizedDropzone onChange={(e) => onChange(e.target.files?.[0])} />
                                        )}
                                    />
                                    {errors.uploaded_file && (
                                        <FormHelperText sx={{ position: 'absolute' }} className='Mui-error'>
                                            File is required
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                        </Box>

                        <Box>
                            <FormControl
                                sx={{ minWidth: '220px', marginRight: '20px' }}
                                error={!!errors.flow_id}
                            >
                                <InputLabel>Select Flow to process file</InputLabel>
                                <Controller
                                    name='flow_id'
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select {...field} label='Select Flow to process file'>
                                            {flowsList?.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.flow_id && (
                                    <FormHelperText sx={{ position: 'absolute', bottom: '-20px' }}>
                                        This is a required field
                                    </FormHelperText>
                                )}
                            </FormControl>
                            <Button
                                sx={styles.button}
                                color='primary'
                                variant='contained'
                                type='submit'
                            >
                                Import File
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ marginBottom: '20px' }} />

                <Box sx={{ marginBottom: '20px' }}>
                    <Box sx={{ position: 'relative' }}>
                        <Typography variant='h6' sx={styles.sectionTitle}>
                            2. Fill in the details
                        </Typography>
                        {responseLoan && (
                            <Tooltip title={
                                <Typography sx={{ fontSize: '1.2rem' }}>
                                    Exclude from data submission
                                </Typography>
                            }>
                                <IconButton
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        right: '30px',
                                        top: '50%',
                                        transform: 'translateY(-50%)'
                                    }}
                                >
                                    <HelpIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <UploadLoanReworkedWithTabs
                        responseLoan={responseLoan}
                        flowsList={flowsList}
                        setResponseJson={setResponseJson}
                        tabsConfig={tabs}
                        fieldsConfig={fieldsConfig}
                    />
                </Box>

                <Divider sx={{ marginBottom: '20px' }} />

                <Box>
                    <Typography variant='h6' sx={styles.sectionTitle}>
                        3. Flow processing results
                    </Typography>
                    {responseJson && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <MemoizedBeautifyJson jsonStr={responseJson} />
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default LoanBoardingPage;
